import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import TEAssignmentTable from './TEAssignmentTable';
import BulkUploadModal from './BulkUploadModal';
import { ToastContainer, useToast } from '../common/Toast';
import { API_BASE } from '../../constants/urls';
import { uploadToS3 } from '../../utils/s3Uploader';

const TEDashboard = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [stats, setStats] = useState(null);
  const { toasts, showSuccess, showError, removeToast, showWarning } = useToast();

  // Fetch assigned articles on component mount
  useEffect(() => {
    fetchAssignments();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/te/stats`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setStats(data.stats);
      }
    } catch (e) { /* silently fail stats */ }
  };


  const fetchAssignments = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await fetch(`${API_BASE}/api/te/assigned`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assignments');
      }

      const data = await response.json();
      setAssignments(data.articles || []);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSingleUpload = async (articleId, file, wordcount, onProgress) => {
    try {
      // Step 1: Upload to S3 directly from browser using proper storage API
      const uploadResult = await uploadToS3(file, articleId, (progress) => {
        if (onProgress) onProgress(progress);
      }, 'submissions'); // Use 'submissions' prefix for TE files

      if (!uploadResult) {
        throw new Error('S3 Upload failed');
      }

      // Step 2: Record submission in backend
      const response = await fetch(`${API_BASE}/api/te/submit-s3`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          article_id: articleId,
          s3_key: uploadResult.s3_key,
          wordcount: wordcount,
          filename: file.name
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Silent refresh to update list without flickering the whole screen
        await fetchAssignments(true);
        showSuccess('✅ Uploaded successfully! Email notification sent to coordinators.');
        return { success: true, message: 'File uploaded successfully!' };
      } else {
        const errorMsg = result.error || 'Submission failed';
        showError(`❌ ${errorMsg}`);
        return { success: false, errors: [errorMsg] };
      }
    } catch (err) {
      console.error('Upload error:', err);
      showError(`❌ ${err.message || 'Network error occurred'}`);
      return { success: false, errors: [err.message || 'Network error occurred'] };
    }
  };

  const handleBulkUpload = async (files, wordCounts, onProgress) => {
    try {
      const totalFiles = files.length;
      const submissions = []; // Collect all submissions for bulk endpoint

      // Step 1: Upload all files to S3 and collect submission data
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const articleId = file.name.split('.')[0];
        const wordcount = wordCounts[file.name];

        // Per-file progress reporting (weighted)
        const updateFileProgress = (fileProgress) => {
            const overallProgress = (i / totalFiles) * 100 + (fileProgress / totalFiles);
            if (onProgress) onProgress(Math.round(overallProgress));
        };

        try {
          // Upload to S3
          const uploadResult = await uploadToS3(file, articleId, updateFileProgress, 'submissions');

          if (!uploadResult?.success) throw new Error("S3 upload failed");

          // Collect submission data
          submissions.push({
            article_id: articleId,
            s3_key: uploadResult.s3_key,
            wordcount: wordcount,
            filename: file.name
          });
        } catch (err) {
          // Log upload error but continue with successful uploads
          console.error(`S3 upload failed for ${file.name}:`, err.message);
        }
      }

      if (onProgress) onProgress(100);

      // Step 2: Submit all collected S3 files via bulk endpoint
      if (submissions.length === 0) {
        showError("❌ No files were successfully uploaded to S3.");
        return { success: false, errors: ["No files uploaded"], successful_uploads: [], failed_uploads: [] };
      }

      const submitResponse = await fetch(`${API_BASE}/api/te/submit-s3/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ submissions })
      });

      const submitResult = await submitResponse.json();

      // Silent refresh
      await fetchAssignments(true);

      if (submitResult.successful_count === files.length) {
        showSuccess(`✅ Successfully uploaded all ${submitResult.successful_count} articles!`);
      } else if (submitResult.successful_count > 0) {
        showWarning(`⚠️ Partial success: ${submitResult.successful_count} uploaded, ${submitResult.failed_count} failed. Wordcount data is preserved for retry.`);
      } else {
        showError('❌ All submissions failed. Wordcount data is preserved for retry.');
      }

      return {
        success: submitResult.failed_count === 0,
        successful_uploads: submitResult.successful_submissions || [],
        failed_uploads: submitResult.failed_submissions || [],
        successful_count: submitResult.successful_count || 0,
        failed_count: submitResult.failed_count || 0
      };
    } catch (err) {
      console.error('Bulk upload error:', err);
      return { success: false, errors: [err.message || 'Network error occurred'], successful_uploads: [], failed_uploads: [] };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p>Loading your assignments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
          <button
            onClick={fetchAssignments}
            className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Upload Articles</h1>
            <p className="mt-1 text-sm text-gray-500">
              Welcome back, {user?.username || 'Technical Editor'}
            </p>
          </div>
          <button
            onClick={() => setShowBulkUpload(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg flex items-center"
          >
            📁 Bulk Upload
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total Assigned', value: stats.total_assigned, color: 'bg-blue-50 border-blue-200 text-blue-700', icon: '📋' },
            { label: 'In Progress', value: stats.wip, color: 'bg-yellow-50 border-yellow-200 text-yellow-700', icon: '⚙️' },
            { label: 'Submitted', value: stats.submitted, color: 'bg-green-50 border-green-200 text-green-700', icon: '✅' },
            { label: 'Total Wordcount', value: stats.total_wordcount.toLocaleString(), color: 'bg-indigo-50 border-indigo-200 text-indigo-700', icon: '📝' },
          ].map(({ label, value, color, icon }) => (
            <div key={label} className={`rounded-xl border p-4 flex flex-col items-center text-center ${color}`}>
              <span className="text-2xl mb-1">{icon}</span>
              <span className="text-2xl font-bold">{value}</span>
              <span className="text-xs font-medium mt-1 opacity-80">{label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900">
              Assigned Articles ({assignments.length})
            </h2>
            <button
              onClick={fetchAssignments}
              className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
            >
              🔄 Refresh
            </button>
          </div>

          <TEAssignmentTable
            assignments={assignments}
            onSingleUpload={handleSingleUpload}
          />
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUploadModal
          assignments={assignments}
          onClose={() => setShowBulkUpload(false)}
          onUpload={handleBulkUpload}
        />
      )}

      {/* Floating Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default TEDashboard;
